import styled, { css } from 'styled-components';
import { StyledFlex, Container, darkColors, secondaryColors, Button } from '@tryhackme/thm-ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const StyledBannerHeader = styled.div`
  background-color: ${secondaryColors.secondary.main};
  width: 100%;
  nav[aria-label='breadcrumb'] {
    li {
      :first-of-type:before {
        padding-right: 0;
      }
    }
  }
`;

export const StyledPathOutlinePathLabel = styled.span`
  color: ${secondaryColors.grey[200]};
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
  font-size: ${({ theme }) => theme.fonts.sizes.default};
`;

export const StyledPathOutlinePathLabelWrapper = styled.span`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

export const StyledViewPathOutlineButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.black};
  border-color: ${({ theme }) => theme.colors.secondary.lighter};
  color: ${({ theme }) => theme.colors.white};
`;

export const StyledBannerHeaderContainer = styled(Container)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.6rem;
`;

export const StyledPathOutlineIcon = styled(FontAwesomeIcon)`
  width: 1.6rem;
  height: 1.6rem;
  color: ${secondaryColors.grey[100]};
`;

export const StyledPathOutlineWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;
`;

export const StyledBanner = styled.div<{
  imageUrl?: string | undefined;
}>`
  flex-direction: column;

  align-items: start;
  display: flex;
  justify-content: space-between;
  position: relative;
  height: auto;
  position: relative;
  background-color: ${({ theme }) => theme.colors.black};
  isolation: isolate;
  min-height: 30rem;
  z-index: 3;

  nav[aria-label='breadcrumb'] {
    li {
      max-width: 40rem;
    }
  }
`;

export const StyledContainer = styled(Container)`
  padding: 2.4rem 1.2rem 0;
`;

export const StyledBannerContent = styled.div<{
  $isBannedOrLockedRoom?: boolean;
  $splitScreenOpen: boolean;
  $isAddCtaPathOutlineRoomsV2ExperimentEnabled: boolean;
}>`
  padding: ${({ $isBannedOrLockedRoom, $isAddCtaPathOutlineRoomsV2ExperimentEnabled }) =>
    $isBannedOrLockedRoom
      ? '7rem 0 4.6rem'
      : `${$isAddCtaPathOutlineRoomsV2ExperimentEnabled ? 0 : '2.4rem'} 0 4.6rem`};

  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    padding-bottom: 3rem;
  }

  ${({ $splitScreenOpen }) =>
    $splitScreenOpen &&
    css`
      padding-bottom: 3rem;
    `}
`;

export const StyledContent = styled(StyledFlex)<{ $splitScreenOpen: boolean }>`
  justify-content: flex-start;
  flex-wrap: nowrap;
  gap: 1.8rem;
  margin-bottom: 4.2rem;

  p {
    max-width: 95rem;
    color: ${({ theme }) => theme.colors.grey[200]};

    a {
      color: ${darkColors.info.main};
    }
  }

  img {
    height: 9rem;
    width: 9rem;
    object-fit: cover;
  }

  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    align-items: start;
    flex-direction: column;
    margin-bottom: 2.4rem;
  }

  ${({ $splitScreenOpen }) =>
    $splitScreenOpen &&
    css`
      align-items: start;
      flex-direction: column;
      margin-bottom: 2.4rem;
    `}
`;

export const StyledTime = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;

  svg {
    font-size: 1.6rem;

    path {
      fill: ${({ theme }) => theme.colors.grey[200]};
    }
  }
`;

export const StyledPaidAccessDaysLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;

  svg {
    font-size: 1.6rem;

    path {
      fill: ${({ theme }) => theme.colors.grey[200]};
    }
  }
`;

export const StyledTitle = styled.h1`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 3.2rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.grey[100]};
`;

export const StyledWrapperMoreInfo = styled(StyledFlex)`
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  margin-top: 1.2rem;
`;

export const StyledImage = styled.div<{ imageUrl?: string | undefined; $splitScreenOpen: boolean }>`
  position: absolute;
  height: 100%;
  width: 100%;
  z-index: -1;
  overflow: hidden;

  img {
    height: 100%;
    left: 10%;
    object-fit: cover;
    object-position: right center;
    position: relative;
    width: 100%;

    @media screen and (min-width: ${({ theme }) => theme.sizes.xl}) {
      left: 40%;
    }

    @media screen and (min-width: ${({ theme }) => theme.sizes.xl}) and (max-width: 1680px) {
      left: 30%;
    }

    @media screen and (min-width: ${({ theme }) => theme.sizes.lg}) and (max-width: ${({ theme }) => theme.sizes.xl}) {
      left: 16%;
    }

    ${({ $splitScreenOpen }) =>
      $splitScreenOpen &&
      css`
        left: 30%;
      `}
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
    height: 100%;
    background-image: linear-gradient(
      to right,
      ${({ theme }) => theme.colors.black} 30%,
      rgba(21, 28, 43, 0.98) 50%,
      transparent
    );

    @media screen and (max-width: ${({ theme }) => theme.sizes.lg}) {
      background: linear-gradient(
        to right,
        ${({ theme }) => theme.colors.black} 60%,
        rgba(21, 28, 43, 0.94),
        transparent
      );
    }
    @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
      background: linear-gradient(
        to right,
        ${({ theme }) => theme.colors.black} 60%,
        rgba(21, 28, 43, 0.95),
        transparent
      );
    }

    ${({ $splitScreenOpen }) =>
      $splitScreenOpen &&
      css`
        background: linear-gradient(
          to right,
          ${({ theme }) => theme.colors.black} 60%,
          rgba(21, 28, 43, 0.95),
          transparent
        );
      `}
  }
`;

export const StyledCollaborator = styled.div`
  span {
    color: ${darkColors.text.light};
  }

  img {
    height: 2.4rem;
    width: auto;
  }
`;
